import React, { useState, useEffect } from "react";
import axios from "axios";
import { BaseURL } from "../../App";

const ProductsDashboard = () => {
  const [products, setProducts] = useState([]);
  const [newProduct, setNewProduct] = useState({
    title: "",
    price: "",
    description: "",
    category: "",
    image: "",
  });
  const [editingProduct, setEditingProduct] = useState(null);
  const [confirmation, setConfirmation] = useState({
    delete: false,
    update: false,
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${BaseURL}/dashboard`);
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!confirmation.delete) {
      // Ask for confirmation
      const confirm = window.confirm("Are you sure you want to delete?");
      if (!confirm) return;
    }

    try {
      await axios.delete(`${BaseURL}/dashboard/products/${productId}`);
      setConfirmation({ ...confirmation, delete: false });
      await updateCache();
      fetchProducts(); // Refresh products after deletion
    } catch (error) {
      console.error("Error deleting product:", error);
    }
  };

  const handleCreateProduct = async () => {
    try {
      await axios.post(`${BaseURL}/dashboard/products`, newProduct);
      setNewProduct({
        title: "",
        price: "",
        description: "",
        category: "",
        image: "",
      });
      await updateCache();
      fetchProducts(); // Refresh products after creation
    } catch (error) {
      console.error("Error creating product:", error);
    }
  };

  const handleEditProduct = async (productId) => {
    const productToEdit = products.find((product) => product._id === productId);
    setEditingProduct(productToEdit);
  };

  const handleUpdateProduct = async () => {
    if (!confirmation.update) {
      // Ask for confirmation
      const confirm = window.confirm("Are you sure you want to update?");
      if (!confirm) return;
    }

    try {
      await axios.put(
        `${BaseURL}/dashboard/products/${editingProduct._id}`,
        editingProduct
      );
      setEditingProduct(null);
      setConfirmation({ ...confirmation, update: false });
      await updateCache();
      fetchProducts(); // Refresh products after update
    } catch (error) {
      console.error("Error updating product:", error);
    }
  };

  const updateCache = async () => {
    try {
      const response = await axios.post(`${BaseURL}/instruments/update-cache`);
      console.log(response.data.message);
    } catch (error) {
      console.error("Error updating cache:", error);
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-3xl font-semibold mb-6 text-center">
        Products Dashboard
      </h2>
      <div className="mb-6">
        <h3 className="text-xl font-semibold mb-3">Create Product</h3>
        {/* Input fields for new product */}
        <input
          className="border rounded-md px-3 py-2 mb-2"
          type="text"
          placeholder="Title"
          value={newProduct.title}
          onChange={(e) =>
            setNewProduct({ ...newProduct, title: e.target.value })
          }
        />
        <input
          className="border rounded-md px-3 py-2 mb-2"
          type="number"
          placeholder="Price"
          value={newProduct.price}
          onChange={(e) =>
            setNewProduct({ ...newProduct, price: e.target.value })
          }
        />
        <input
          className="border rounded-md px-3 py-2 mb-2"
          type="text"
          placeholder="Description"
          value={newProduct.description}
          onChange={(e) =>
            setNewProduct({ ...newProduct, description: e.target.value })
          }
        />
        <input
          className="border rounded-md px-3 py-2 mb-2"
          type="text"
          placeholder="Category"
          value={newProduct.category}
          onChange={(e) =>
            setNewProduct({ ...newProduct, category: e.target.value })
          }
        />
        <input
          className="border rounded-md px-3 py-2 mb-2"
          type="text"
          placeholder="Image URL" // Input for image URL
          value={newProduct.image}
          onChange={(e) =>
            setNewProduct({ ...newProduct, image: e.target.value })
          }
        />
        <button
          className="bg-blue-950 text-white px-4 py-2 rounded-md hover:bg-blue-900"
          onClick={handleCreateProduct}
        >
          Create
        </button>
      </div>
      <table className="w-full">
        <thead>
          <tr className="bg-gray-200">
            <th className="px-4 py-2">Title</th>
            <th className="px-4 py-2">Price</th>
            <th className="px-4 py-2">Description</th>
            <th className="px-4 py-2">Category</th>
            <th className="px-4 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {products.map((product) => (
            <tr key={product._id} className="border-b">
              <td className="px-4 py-2">
                {editingProduct && editingProduct._id === product._id ? (
                  <input
                    className="border rounded-md px-3 py-1"
                    type="text"
                    value={editingProduct.title}
                    onChange={(e) =>
                      setEditingProduct({
                        ...editingProduct,
                        title: e.target.value,
                      })
                    }
                  />
                ) : (
                  product.title
                )}
              </td>
              <td className="px-4 py-2">
                {editingProduct && editingProduct._id === product._id ? (
                  <input
                    className="border rounded-md px-3 py-1"
                    type="number"
                    value={editingProduct.price}
                    onChange={(e) =>
                      setEditingProduct({
                        ...editingProduct,
                        price: e.target.value,
                      })
                    }
                  />
                ) : (
                  product.price
                )}
              </td>
              <td className="px-4 py-2">
                {editingProduct && editingProduct._id === product._id ? (
                  <input
                    className="border rounded-md px-3 py-1"
                    type="text"
                    value={editingProduct.description}
                    onChange={(e) =>
                      setEditingProduct({
                        ...editingProduct,
                        description: e.target.value,
                      })
                    }
                  />
                ) : (
                  product.description
                )}
              </td>
              <td className="px-4 py-2">
                {editingProduct && editingProduct._id === product._id ? (
                  <input
                    className="border rounded-md px-3 py-1"
                    type="text"
                    value={editingProduct.category}
                    onChange={(e) =>
                      setEditingProduct({
                        ...editingProduct,
                        category: e.target.value,
                      })
                    }
                  />
                ) : (
                  product.category
                )}
              </td>
              <td className="px-4 py-2">
                {editingProduct && editingProduct._id === product._id ? (
                  <>
                    <button
                      className="bg-green-500 text-white px-4 py-2 rounded-md mr-2 hover:bg-green-600"
                      onClick={handleUpdateProduct}
                    >
                      Update
                    </button>
                    <button
                      className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
                      onClick={() => setEditingProduct(null)}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="bg-blue-950 text-white px-4 py-2 rounded-md mr-2 hover:bg-blue-900 m-2"
                      onClick={() => handleEditProduct(product._id)}
                    >
                      Edit
                    </button>
                    <button
                      className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600"
                      onClick={() => {
                        setConfirmation({ ...confirmation, delete: true });
                        handleDeleteProduct(product._id);
                      }}
                    >
                      Delete
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ProductsDashboard;
